/**
 * @fileoverview DEPRECATED: This entire file is deprecated.
 * 
 * Use the HideableValue utilities from `@wix/devzai-utils-common` instead.
 * 
 * ## Migration Guide
 * 
 * Replace all Doppe-specific HideableValue functions with their common equivalents:
 * 
 * ```typescript
 * // Old imports
 * import {
 *     DoppeHideableValue,
 *     DoppeHideableValueHiddenState,
 *     doppeHideableValueIsVisible,
 *     doppeHideableValueIsHidden,
 *     doppeHideableValueGetValue,
 *     doppeHideableValueCreateHidden,
 *     // ... other functions
 * } from './doppe-hideable-value';
 * 
 * // New imports
 * import {
 *     HideableValue,
 *     HideableValueHiddenState,
 *     hideableValueIsVisible,
 *     hideableValueIsHidden,
 *     hideableValueGetValue,
 *     hideableValueCreateHidden,
 *     // ... other functions
 * } from '@wix/devzai-utils-common';
 * ```
 * 
 * The API is identical - just remove the `doppe` prefix and update the import source.
 */

import {
    AssertionLogicFunction,
    assertType,
    Evaluable, evaluateWhenFunction,
    NarrowedNonUnionTypeAssertController
} from "@wix/devzai-utils-common";

/**
 * @deprecated Use {@link HideableValueHiddenState} from `@wix/devzai-utils-common` instead.
 */
export type DoppeHideableValueHiddenState<V> = {
    __hidden: true;
    value: V;
};

/**
 * @deprecated Use {@link HideableValue} from `@wix/devzai-utils-common` instead.
 */
export type DoppeHideableValue<V> = V | DoppeHideableValueHiddenState<V>;

/**
 * @deprecated Use {@link HideableValue.ResolveValueType} from `@wix/devzai-utils-common` instead.
 */
export namespace DoppeHideableValue {
    export type ResolveValueType<T> = T extends DoppeHideableValue<infer U> ? U : T;
}

/**
 * @deprecated Use {@link hideableValueIsVisibleAndNotEqualValue} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueIsVisibleAndNotEqualValue<V> (value: any, comparedValue: V) : value is V {
    return !doppeHideableValueIsHidden(value) && doppeHideableValueGetValue(value) !== comparedValue;
}

/**
 * @deprecated Use {@link hideableValueIsVisible} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueIsVisible<V> (value: DoppeHideableValue<V>) : value is V {
    return !doppeHideableValueIsHidden(value);
}

/**
 * @deprecated Use {@link hideableValueIsHidden} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueIsHidden<V> (value: any) : value is DoppeHideableValueHiddenState<V> {
    return typeof value === 'object' && value !== null && (value as DoppeHideableValueHiddenState<V>).__hidden === true;
}

/**
 * @deprecated Use {@link hideableValueGetValue} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueGetValue<V> (value: DoppeHideableValue<V>) : V {
    if (doppeHideableValueIsHidden<V>(value)) {
        return value.value;
    } else {
        return value;
    }
}

/**
 * @deprecated Use {@link hideableValueModifyValue} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueModifyValue<V> (
    hideableValue: DoppeHideableValue<V>,
    modificationFunction: Evaluable<(value: V) => V>
) : DoppeHideableValue<V> {
    const value = doppeHideableValueGetValue(hideableValue);

    const modifiedValue = evaluateWhenFunction(modificationFunction, value);

    if (doppeHideableValueIsHidden(hideableValue)) {
        return doppeHideableValueCreateHidden(modifiedValue);
    } else {
        return modifiedValue;
    }
}

/**
 * @deprecated Use {@link hideableValueGetVisibleValue} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueGetVisibleValue<V, H> (value: DoppeHideableValue<V>, hiddenValue: H) : V | H {
    if (doppeHideableValueIsHidden<V>(value)) {
        return hiddenValue;
    } else {
        return value;
    }
}

/**
 * @deprecated Use {@link hideableValueCreateHidden} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueCreateHidden<V> (value: V) : DoppeHideableValueHiddenState<V> {
    return {
        __hidden: true,
        value: value
    }
}

/**
 * @deprecated Use {@link hideableValueEnsureHidden} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueEnsureHidden<V> (value: DoppeHideableValue<V>) : DoppeHideableValueHiddenState<V> {
    if (doppeHideableValueIsHidden(value)) {
        return value;
    } else {
        return doppeHideableValueCreateHidden(value);
    }
}

/**
 * @deprecated Use {@link hideableValueToggleVisible} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueToggleVisible<V> (
    value: DoppeHideableValue<V>,
    isVisible: Evaluable<(isVisible: boolean) => boolean>
) : DoppeHideableValue<V> {
    return doppeHideableValueToggleHidden(value, wasHidden => !evaluateWhenFunction(isVisible, !wasHidden))
}

/**
 * @deprecated Use {@link hideableValueToggleHidden} from `@wix/devzai-utils-common` instead.
 */
export function doppeHideableValueToggleHidden<V> (
    value: DoppeHideableValue<V>,
    isHidden: Evaluable<(isHidden: boolean) => boolean>
) : DoppeHideableValue<V> {
    const wasHidden = doppeHideableValueIsHidden(value);

    const evaluatedIsHidden = evaluateWhenFunction(isHidden, wasHidden);

    if (wasHidden !== evaluatedIsHidden) {
        return wasHidden ? value.value : doppeHideableValueCreateHidden(value);
    } else {
        return value;
    }
}

/**
 * @deprecated Use {@link assertHideableValue} from `@wix/devzai-utils-common` instead.
 */
export function assertDoppeHideableValue<V> (doppeHideableValue: DoppeHideableValue<V>, assertionLogic: AssertionLogicFunction<V>) : asserts doppeHideableValue is DoppeHideableValue<V>;
export function assertDoppeHideableValue<V> (doppeHideableValue: unknown, assertionLogic: AssertionLogicFunction<V>) : asserts doppeHideableValue is DoppeHideableValue<V>;
export function assertDoppeHideableValue<V> (doppeHideableValue: unknown, assertionLogic: AssertionLogicFunction<V>) : asserts doppeHideableValue is DoppeHideableValue<V> {
    assertType<DoppeHideableValue<V>>(doppeHideableValue, assert => {
        (assert as any).isUnion(
            assertionLogic,
            (assert: NarrowedNonUnionTypeAssertController<DoppeHideableValueHiddenState<V>>) => assert.isObject({
                __hidden: assert => assert.isOneOfValues([true]),
                value: assertionLogic
            })
        )
    })
}